import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import * as LucideIcons from "lucide-react";
import { ArrowUpRight } from "lucide-react";
import { products } from "@/data/landing";
import { fadeUp, staggerContainer, staggerItem } from "@/lib/motion";

const ProductCard = ({ p, idx }) => {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ["start end", "end start"],
    });
    const y = useTransform(scrollYProgress, [0, 1], [80, -80]);
    const imageY = useTransform(scrollYProgress, [0, 1], [-30, 30]);

    return (
        <motion.article
            ref={ref}
            variants={staggerItem}
            data-testid={`product-card-${p.id}`}
            className="group relative glass rounded-3xl overflow-hidden"
            style={{ y }}
        >
            {/* animated conic border on hover */}
            <motion.div
                aria-hidden
                className="pointer-events-none absolute -inset-px rounded-3xl opacity-0 group-hover:opacity-100 transition duration-500"
                style={{
                    background:
                        "conic-gradient(from 0deg, transparent, rgba(0,229,255,0.4), transparent 30%, rgba(124,92,255,0.4), transparent 60%)",
                }}
                animate={{ rotate: 360 }}
                transition={{ duration: 10, repeat: Infinity, ease: "linear" }}
            />
            <div className="relative rounded-3xl bg-[#0a0a0c]">
                <div
                    className="absolute pointer-events-none -top-32 -right-20 w-[420px] h-[420px] rounded-full blur-[140px] opacity-60"
                    style={{ background: p.glow }}
                />
                <div className="relative grid lg:grid-cols-12 gap-8 lg:gap-12 p-8 md:p-12">
                    {/* Left: brand + features */}
                    <div className="lg:col-span-7 flex flex-col">
                        <div className="flex items-center gap-3 flex-wrap">
                            <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-cyan-300 bg-cyan-400/10 border border-cyan-400/20 rounded-full px-3 py-1">
                                {p.tag}
                            </span>
                            <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">
                                Product 0{idx + 1}
                            </span>
                        </div>

                        <h3 className="mt-5 font-display font-bold text-white text-3xl md:text-4xl lg:text-5xl tracking-tight">
                            {p.name}
                        </h3>
                        <p className="mt-3 text-zinc-300 text-lg leading-relaxed max-w-2xl">
                            {p.headline}
                        </p>
                        <p className="mt-3 text-zinc-500 text-[15px] leading-relaxed max-w-2xl">
                            {p.long}
                        </p>

                        <div className="mt-7 grid sm:grid-cols-2 gap-3">
                            {p.features.map((f, i) => {
                                const Icon =
                                    LucideIcons[f.icon] || LucideIcons.Sparkles;
                                return (
                                    <motion.div
                                        key={f.label}
                                        initial={{ opacity: 0, x: -20 }}
                                        whileInView={{ opacity: 1, x: 0 }}
                                        viewport={{ once: true }}
                                        transition={{ delay: i * 0.05 + 0.2 }}
                                        whileHover={{ x: 4 }}
                                        className="flex items-center gap-3 rounded-xl bg-white/[0.03] border border-white/5 px-4 py-3 hover:bg-white/[0.08] hover:border-cyan-400/30 transition cursor-default"
                                    >
                                        <span className="h-8 w-8 rounded-md bg-cyan-400/10 border border-cyan-400/20 grid place-items-center shrink-0">
                                            <Icon
                                                className="h-4 w-4 text-cyan-300"
                                                strokeWidth={1.75}
                                            />
                                        </span>
                                        <span className="text-sm text-zinc-200">
                                            {f.label}
                                        </span>
                                    </motion.div>
                                );
                            })}
                        </div>

                        <div className="mt-8 flex flex-wrap items-center gap-4">
                            <a
                                href={p.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid={`product-cta-${p.id}`}
                                className="btn-shine inline-flex items-center gap-2 rounded-lg bg-cyan-400 hover:bg-cyan-300 text-black font-semibold px-6 py-3.5 transition shadow-[0_0_30px_-10px_rgba(0,229,255,0.6)]"
                            >
                                {p.cta}
                                <ArrowUpRight className="h-4 w-4" />
                            </a>
                            <span className="font-mono text-xs text-zinc-500">
                                {p.url
                                    .replace(/^https?:\/\//, "")
                                    .replace(/\/$/, "")}
                            </span>
                        </div>
                    </div>

                    {/* Right: stats + parallax image */}
                    <div className="lg:col-span-5 flex flex-col gap-6">
                        <motion.div
                            style={{ y: imageY }}
                            className="relative rounded-2xl overflow-hidden border border-white/10 aspect-[4/3] bg-black"
                        >
                            <img
                                src={p.image}
                                alt={p.name}
                                className="w-full h-full object-cover opacity-90 group-hover:scale-105 group-hover:opacity-100 transition duration-700"
                                loading="lazy"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-transparent" />
                            <div className="absolute top-3 left-3 inline-flex items-center gap-1.5 rounded-full bg-black/60 backdrop-blur-md border border-white/10 px-3 py-1 text-[10px] font-mono uppercase tracking-[0.2em] text-cyan-300">
                                <span className="h-1.5 w-1.5 rounded-full bg-cyan-400 animate-pulse" />
                                Live Product
                            </div>
                        </motion.div>
                        <div className="grid grid-cols-3 gap-3">
                            {p.stats.map((s) => (
                                <div
                                    key={s.l}
                                    className="rounded-xl bg-white/[0.03] border border-white/10 p-4 text-center hover:border-cyan-400/30 hover:bg-white/[0.06] transition"
                                >
                                    <div className="font-mono text-xl md:text-2xl font-bold text-white">
                                        {s.v}
                                    </div>
                                    <div className="mt-1 text-[10px] uppercase tracking-[0.18em] text-zinc-500">
                                        {s.l}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </motion.article>
    );
};

const Products = () => {
    return (
        <section
            id="products"
            data-testid="products-section"
            className="section py-24 md:py-32"
        >
            <div className="absolute inset-0 bg-grid opacity-40 pointer-events-none" />

            <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10">
                <motion.div {...fadeUp} className="max-w-3xl mb-16">
                    <span className="font-mono text-xs uppercase tracking-[0.3em] text-cyan-300">
                        03 / Our Products
                    </span>
                    <h2
                        data-testid="products-heading"
                        className="mt-4 font-display font-semibold text-white text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight"
                    >
                        Four flagships.
                        <br />
                        <span className="text-grad-cyan">One mission.</span>
                    </h2>
                    <p className="mt-5 text-zinc-400 text-lg">
                        Software that thousands of Indian aspirants and advocates open
                        every single morning.
                    </p>
                </motion.div>

                <motion.div
                    variants={staggerContainer}
                    initial="initial"
                    whileInView="whileInView"
                    viewport={{ once: true, margin: "-100px" }}
                    className="space-y-8 md:space-y-10"
                >
                    {products.map((p, idx) => (
                        <ProductCard key={p.id} p={p} idx={idx} />
                    ))}
                </motion.div>
            </div>
        </section>
    );
};

export default Products;
